/**
 * Web3Context - Unified wallet, network, and contract management for React.
 *
 * Features:
 * - EIP-6963 wallet detection (MetaMask preference)
 * - Arbitrum One network management with auto-add
 * - ABI loading from backend API
 * - Contract factory with signer/read-only modes
 * - Silent auto-connect if previously authorized
 * - Event listeners for account/chain changes
 * - Backend session synchronization
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode
} from 'react';
import {
  BrowserProvider,
  JsonRpcProvider,
  Contract,
  type Signer,
  type Eip1193Provider,
  type InterfaceAbi
} from 'ethers';

// ============================================================================
// Types
// ============================================================================

// Extended provider type with event methods (MetaMask/wallet extensions)
interface ExtendedEip1193Provider extends Eip1193Provider {
  isMetaMask?: boolean;
  providers?: Eip1193Provider[];
  on?: (event: string, handler: (...args: any[]) => void) => void;
  removeListener?: (event: string, handler: (...args: any[]) => void) => void;
}

interface NetworkConfig {
  chainId: number;
  chainIdHex: string;
  chainName: string;
  rpcUrls: string[];
  blockExplorerUrls: string[];
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
}

interface Web3ContextType {
  // Providers
  provider: JsonRpcProvider | BrowserProvider | null;
  providerReadOnly: JsonRpcProvider | null;
  signer: Signer | null;

  // State
  account: string | null;
  chainId: number | null;
  isConnected: boolean;
  isConnecting: boolean;
  isInitialized: boolean;

  // ABIs
  abis: Record<string, InterfaceAbi>;
  getAbi: (name: string) => InterfaceAbi;

  // Contract factory
  getContract: (address: string, abi: InterfaceAbi, mode?: 'auto' | 'signer' | 'readOnly') => Contract;

  // Actions
  connect: () => Promise<string | null>;
  disconnect: () => Promise<void>;
  switchNetwork: () => Promise<void>;

  // Config
  contractAddress: string;
  network: NetworkConfig;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * RetryProvider - JsonRpcProvider with sequential per-call fallback.
 *
 * Every RPC call tries the primary URL first. On failure (CORS, rate limit,
 * network error), it retries with the next URL in the list. This fixes the
 * issue where ethers FallbackProvider keeps routing to a partially-failing
 * primary and never actually falls back on browser CORS errors.
 */
class RetryProvider extends JsonRpcProvider {
  private fallbacks: JsonRpcProvider[];
  private urls: string[];

  constructor(urls: string[]) {
    super(urls[0]);
    this.urls = urls;
    this.fallbacks = urls.slice(1).map(url => new JsonRpcProvider(url));
    console.log(
      `[Web3] RetryProvider initialized with ${urls.length} RPCs:\n` +
      urls.map((u, i) => `  ${i + 1}. ${u}`).join('\n')
    );
  }

  async send(method: string, params: Array<any>): Promise<any> {
    // Try primary (this instance)
    try {
      return await super.send(method, params);
    } catch (primaryError: any) {
      // Don't retry on contract reverts - they'll fail on all RPCs
      // Contract reverts have code 'CALL_EXCEPTION' and are legitimate business logic errors
      if (primaryError?.code === 'CALL_EXCEPTION') {
        throw primaryError;
      }

      // Only retry on network/RPC errors (CORS, timeout, 429, 500, etc.)
      for (let i = 0; i < this.fallbacks.length; i++) {
        try {
          console.warn(
            `[Web3] RPC #1 (${this.urls[0]}) failed for ${method}, ` +
            `trying #${i + 2} (${this.urls[i + 1]})...`
          );
          return await this.fallbacks[i].send(method, params);
        } catch {
          // This fallback also failed, continue to next
        }
      }
      // All RPCs exhausted
      console.error(`[Web3] All ${this.urls.length} RPCs failed for ${method}`);
      throw primaryError;
    }
  }
}

const ARBITRUM_CONFIG: NetworkConfig = {
  chainId: 42161,
  chainIdHex: '0xa4b1',
  chainName: 'Arbitrum One',
  rpcUrls: [
    // Primary: Official Arbitrum Foundation RPC (most reliable)
    'https://arb1.arbitrum.io/rpc',
    // Fallback: PublicNode - proven CORS support, reliable, free
    'https://arbitrum-one.publicnode.com',
    // Fallback: 1RPC - privacy-focused, proper CORS
    'https://1rpc.io/arb'
  ],
  blockExplorerUrls: ['https://arbiscan.io/'],
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18
  }
};

// Contract address: loaded from server at runtime (set via SUBS_CONTRACT_ADDR env var)

// ============================================================================
// Context
// ============================================================================

const Web3Context = createContext<Web3ContextType | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

export const Web3Provider = ({ children }: { children: ReactNode }) => {
  // Providers
  const [providerReadOnly, setProviderReadOnly] = useState<JsonRpcProvider | null>(null);
  const [provider, setProvider] = useState<JsonRpcProvider | BrowserProvider | null>(null);
  const [signer, setSigner] = useState<Signer | null>(null);

  // State
  const [account, setAccount] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // ABIs & contract address (loaded from server at runtime)
  const [abis, setAbis] = useState<Record<string, InterfaceAbi>>({});
  const [contractAddress, setContractAddress] = useState<string>('');

  // ============================================================================
  // Wallet Detection (EIP-6963 + legacy)
  // ============================================================================

  const selectInjectedProvider = useCallback((preferMetaMask = true): ExtendedEip1193Provider | null => {
    const candidates: ExtendedEip1193Provider[] = [];

    // EIP-6963 announce (modern wallets)
    try {
      const handleAnnounce = (ev: CustomEvent) => {
        if (ev.detail?.provider) {
          candidates.push(ev.detail.provider);
        }
      };
      window.addEventListener('eip6963:announceProvider', handleAnnounce as EventListener, { once: true });
      window.dispatchEvent(new Event('eip6963:requestProvider'));
    } catch {
      // EIP-6963 not supported
    }

    // Legacy window.ethereum
    if (window.ethereum) {
      const list = (window.ethereum as any).providers?.length
        ? (window.ethereum as any).providers
        : [window.ethereum];
      candidates.push(...list);
    }

    // Prefer MetaMask
    if (preferMetaMask) {
      const mm = candidates.find((p: any) => p?.isMetaMask);
      if (mm) return mm;
    }

    return candidates[0] || null;
  }, []);

  // ============================================================================
  // Network Management
  // ============================================================================

  const switchNetwork = useCallback(async () => {
    const injected = selectInjectedProvider();
    if (!injected) return;

    const browserProvider = new BrowserProvider(injected);

    try {
      await browserProvider.send('wallet_switchEthereumChain', [
        { chainId: ARBITRUM_CONFIG.chainIdHex }
      ]);
    } catch (err: any) {
      // Chain not added - add it
      if (err?.code === 4902) {
        await browserProvider.send('wallet_addEthereumChain', [{
          chainId: ARBITRUM_CONFIG.chainIdHex,
          chainName: ARBITRUM_CONFIG.chainName,
          nativeCurrency: ARBITRUM_CONFIG.nativeCurrency,
          rpcUrls: ARBITRUM_CONFIG.rpcUrls,
          blockExplorerUrls: ARBITRUM_CONFIG.blockExplorerUrls
        }]);
        // Try switching again after adding
        await browserProvider.send('wallet_switchEthereumChain', [
          { chainId: ARBITRUM_CONFIG.chainIdHex }
        ]);
      } else if (err?.code === 4001) {
        // User rejected
        throw new Error('User rejected network switch');
      } else {
        throw err;
      }
    }
  }, [selectInjectedProvider]);

  // ============================================================================
  // Connect Wallet
  // ============================================================================

  const connect = useCallback(async (): Promise<string | null> => {
    const injected = selectInjectedProvider();
    if (!injected) {
      console.error('No crypto wallet found');
      return null;
    }

    setIsConnecting(true);

    try {
      const browserProvider = new BrowserProvider(injected);

      // Request accounts
      await browserProvider.send('eth_requestAccounts', []);

      // Check network
      const network = await browserProvider.getNetwork();
      if (Number(network.chainId) !== ARBITRUM_CONFIG.chainId) {
        await switchNetwork();
      }

      // Get signer and address
      const walletSigner = await browserProvider.getSigner();
      const address = await walletSigner.getAddress();

      // Update state
      setProvider(browserProvider);
      setSigner(walletSigner);
      setAccount(address);
      setChainId(Number(network.chainId));

      // Sync with backend session
      try {
        await fetch('/connect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ wallet_address: address })
        });
      } catch (e) {
        console.warn('Failed to sync session with backend:', e);
      }

      return address;
    } catch (e) {
      console.error('Connection failed:', e);
      return null;
    } finally {
      setIsConnecting(false);
    }
  }, [selectInjectedProvider, switchNetwork]);

  // ============================================================================
  // Disconnect Wallet
  // ============================================================================

  const disconnect = useCallback(async () => {
    setSigner(null);
    setAccount(null);
    setChainId(null);

    // Revert to read-only provider
    if (providerReadOnly) {
      setProvider(providerReadOnly);
    }

    // Clear backend session
    try {
      await fetch('/disconnect', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.warn('Failed to clear backend session:', e);
    }
  }, [providerReadOnly]);

  // ============================================================================
  // Silent Auto-Connect
  // ============================================================================

  const silentConnect = useCallback(async () => {
    const injected = selectInjectedProvider();
    if (!injected) return;

    try {
      // Check if already authorized (doesn't prompt user)
      const browserProvider = new BrowserProvider(injected);
      const accounts = await browserProvider.send('eth_accounts', []);

      if (accounts && accounts.length > 0) {
        const walletSigner = await browserProvider.getSigner();
        const address = await walletSigner.getAddress();
        const network = await browserProvider.getNetwork();

        setProvider(browserProvider);
        setSigner(walletSigner);
        setAccount(address);
        setChainId(Number(network.chainId));

        // Sync with backend
        try {
          await fetch('/connect', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ wallet_address: address })
          });
        } catch {
          // Silently fail
        }
      }
    } catch {
      // Silently fail
    }
  }, [selectInjectedProvider]);

  // ============================================================================
  // ABI Helpers
  // ============================================================================

  const getAbi = useCallback((name: string): InterfaceAbi => {
    const abi = abis[name];
    if (!abi) {
      throw new Error(`ABI '${name}' not found. Available: ${Object.keys(abis).join(', ')}`);
    }
    return abi;
  }, [abis]);

  // ============================================================================
  // Contract Factory
  // ============================================================================

  const getContract = useCallback((
    address: string,
    abi: InterfaceAbi,
    mode: 'auto' | 'signer' | 'readOnly' = 'auto'
  ): Contract => {
    if (mode === 'readOnly') {
      if (!providerReadOnly) {
        throw new Error('Read-only provider not initialized');
      }
      return new Contract(address, abi, providerReadOnly);
    }

    if (mode === 'signer') {
      if (!signer) {
        throw new Error('No signer available. Please connect wallet first.');
      }
      return new Contract(address, abi, signer);
    }

    // Auto mode: prefer signer, fallback to read-only
    if (signer) {
      return new Contract(address, abi, signer);
    }

    if (providerReadOnly) {
      return new Contract(address, abi, providerReadOnly);
    }

    throw new Error('No provider available');
  }, [signer, providerReadOnly]);

  // ============================================================================
  // Initialization
  // ============================================================================

  useEffect(() => {
    const init = async () => {
      // Initialize read-only provider with per-call sequential retry:
      // Every RPC call tries the primary, then fallbacks on failure.
      const roProvider = new RetryProvider(ARBITRUM_CONFIG.rpcUrls);
      setProviderReadOnly(roProvider);
      setProvider(roProvider);

      // Load ABIs from backend
      try {
        const response = await fetch('/api/abis');
        if (response.ok) {
          const data = await response.json();
          setAbis(data.abis || {});
          setContractAddress(data.contractAddress || '');
          console.log(`[Web3] Contract address from server: ${data.contractAddress || '(empty)'}`);
        } else {
          console.warn('Failed to load ABIs from backend');
        }
      } catch (e) {
        console.warn('Failed to fetch ABIs:', e);
      }

      // Try silent connect
      await silentConnect();

      setIsInitialized(true);
    };

    init();
  }, [silentConnect]);

  // ============================================================================
  // Wallet Event Listeners
  // ============================================================================

  useEffect(() => {
    const injected = selectInjectedProvider();
    if (!injected) return;

    const handleAccountsChanged = async (accounts: string[]) => {
      if (accounts.length === 0) {
        // User disconnected
        await disconnect();
      } else if (signer) {
        // Account changed - reconnect
        await connect();
      }
    };

    const handleChainChanged = (chainIdHex: string) => {
      const newChainId = parseInt(chainIdHex, 16);
      setChainId(newChainId);

      // Reload if not on Arbitrum
      if (newChainId !== ARBITRUM_CONFIG.chainId) {
        window.location.reload();
      }
    };

    const handleDisconnect = () => {
      disconnect();
    };

    // Add listeners
    injected.on?.('accountsChanged', handleAccountsChanged);
    injected.on?.('chainChanged', handleChainChanged);
    injected.on?.('disconnect', handleDisconnect);

    return () => {
      // Remove listeners
      injected.removeListener?.('accountsChanged', handleAccountsChanged);
      injected.removeListener?.('chainChanged', handleChainChanged);
      injected.removeListener?.('disconnect', handleDisconnect);
    };
  }, [selectInjectedProvider, signer, connect, disconnect]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: Web3ContextType = {
    provider,
    providerReadOnly,
    signer,
    account,
    chainId,
    isConnected: !!account,
    isConnecting,
    isInitialized,
    abis,
    getAbi,
    getContract,
    connect,
    disconnect,
    switchNetwork,
    contractAddress,
    network: ARBITRUM_CONFIG
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useWeb3 = (): Web3ContextType => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};

